import { get, post, postAttachmentData, put, remove } from './mainApi';
import { generateId } from 'src/utils/traceIdGenerations';

const attendanceBaseUrl =
  process.env.REACT_APP_LEAVE_MANAGEMENT_SERVICE_V1_ENDPOINT + 'attendance';

export type DailyAttendance = {
  date: string;
  weekend?: boolean;
  holiday?: boolean;
  /** Holiday description from the company calendar, such as Poya Day. */
  holidayName?: string | null;
  /** Public Holiday, Mercantile Holiday, Bank Holiday or Company Holiday. */
  holidayType?: string | null;
  /** Full Day, Half Day Morning or Half Day Evening. Empty when the day is not a holiday. */
  holidaySession?: string | null;
  inTime?: string | null;
  display?: string | null;
  leaveType?: string | null;
  onLeave?: boolean;
  halfDay?: boolean;
  /** Morning or Evening for a half-day or short leave. */
  session?: string | null;
  late?: boolean;
};

export type AttendanceEmployeeRow = {
  employeeId: string;
  employeeNumber?: string | null;
  name: string;
  department?: string | null;
  role?: string | null;
  projectIds?: string[];
  days?: DailyAttendance[];
};

export type AttendanceProjectOption = {
  projectId: string;
  projectName: string;
};

export type EmployeeAttendanceResponse = {
  employeeId: string;
  fromDate: string;
  toDate: string;
  empty: boolean;
  emptyMessage?: string | null;
  days: DailyAttendance[];
};

export type AttendanceGridResponse = {
  scope?: string;
  fromDate: string;
  toDate: string;
  projectIds?: string[];
  lateAfter?: string | null;
  projects?: AttendanceProjectOption[];
  empty: boolean;
  emptyMessage?: string | null;
  employees: AttendanceEmployeeRow[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
};

export type AttendanceGridQuery = {
  fromDate: string;
  toDate: string;
  employees?: string[];
  departments?: string[];
  projects?: string[];
  search?: string;
  pageNo?: number;
  pageSize?: number;
};

const withTraceId = (url: string) => {
  const traceId = generateId(9);
  return url.includes('?') ? `${url}&traceId=${traceId}` : `${url}?traceId=${traceId}`;
};

const appendList = (params: URLSearchParams, key: string, values?: string[]) => {
  const selected = (values || []).map((value) => value.trim()).filter(Boolean);
  if (selected.length) {
    params.set(key, selected.join(','));
  }
};

const gridParams = (query: AttendanceGridQuery, includePage: boolean) => {
  const params = new URLSearchParams();
  params.set('fromDate', query.fromDate);
  params.set('toDate', query.toDate);
  appendList(params, 'employees', query.employees);
  appendList(params, 'departments', query.departments);
  appendList(params, 'projects', query.projects);
  if (query.search?.trim()) {
    params.set('search', query.search.trim());
  }
  if (includePage) {
    params.set('pageNo', String(query.pageNo ?? 1));
    params.set('pageSize', String(query.pageSize ?? 10));
  }
  return params.toString();
};

export const getMyAttendance = (fromDate: string, toDate: string) => {
  const params = new URLSearchParams({ fromDate, toDate });
  return get(withTraceId(`${attendanceBaseUrl}/me?${params.toString()}`));
};

export const getAttendanceGrid = (query: AttendanceGridQuery) => {
  return get(withTraceId(`${attendanceBaseUrl}?${gridParams(query, true)}`));
};

export const exportAttendanceGrid = (query: AttendanceGridQuery) => {
  return get(withTraceId(`${attendanceBaseUrl}/export?${gridParams(query, false)}`), 'arraybuffer');
};

export const importAttendance = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return postAttachmentData(withTraceId(`${attendanceBaseUrl}/import`), form);
};

export type EmployeeDeviceMapping = {
  employeeId: string;
  employeeName: string;
  deviceId: string;
};

export type EmployeeDeviceMappingPage = {
  employees: EmployeeDeviceMapping[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
};

export const getEmployeeMappings = (pageNo: number, pageSize: number) => {
  const params = new URLSearchParams({
    pageNo: String(pageNo),
    pageSize: String(pageSize)
  });
  return get(withTraceId(`${attendanceBaseUrl}/employee-mappings?${params.toString()}`));
};

export const addEmployeeMapping = (employeeId: string, deviceId: string) => {
  return post(withTraceId(`${attendanceBaseUrl}/employee-mappings`), { employeeId, deviceId });
};

export const updateEmployeeMapping = (employeeId: string, deviceId: string) => {
  return put(withTraceId(`${attendanceBaseUrl}/employee-mappings`), { employeeId, deviceId });
};

export const removeEmployeeMapping = (employeeId: string) => {
  const params = new URLSearchParams({ employeeId });
  return remove(withTraceId(`${attendanceBaseUrl}/employee-mappings?${params.toString()}`));
};

export type AttendanceRowError = {
  row: number | null;
  column: string;
  message: string;
};

export type AttendanceApiError = {
  statusCode?: number;
  message: string;
  /** Row/column problems reported by the API, minus any that only repeat `message`. */
  errors: AttendanceRowError[];
};

const responseBody = (error: any) => {
  const data = error?.response?.data;
  if (!(data instanceof ArrayBuffer)) {
    return data;
  }
  try {
    return JSON.parse(new TextDecoder().decode(data));
  } catch {
    return null;
  }
};

/** The API error response as returned by LMS: summary message plus row-level errors. */
export const attendanceApiError = (error: any, fallback: string): AttendanceApiError => {
  const body = responseBody(error);
  const message = String(body?.message || '').trim() || fallback;
  const items: any[] = Array.isArray(body?.data) ? body.data : [];
  const errors = items
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const row = item.row ?? item.rowNumber;
      return {
        row: row != null && row !== '' && Number.isFinite(Number(row)) ? Number(row) : null,
        column: String(item.column || '').trim(),
        message: String(item.message || '').trim()
      };
    })
    .filter((item) => item.message || item.row != null)
    .filter((item) => !(item.row == null && item.message === message));
  const repeatsSummary = errors.length === 1 && errors[0].message === message;
  return {
    statusCode: body?.statusCode ?? error?.response?.status,
    message: repeatsSummary ? 'Attendance file has 1 error.' : message,
    errors
  };
};

export const attendanceErrorMessage = (error: any, fallback: string) => {
  const { message, errors } = attendanceApiError(error, fallback);
  const details = errors.map((item) => {
    const where = [item.row != null ? `Row ${item.row}` : '', item.column].filter(Boolean).join(', ');
    return where && item.message ? `${where}: ${item.message}` : item.message || where;
  });
  return [message, ...details].join('\n');
};
